import { Injectable, computed, inject, signal } from '@angular/core';
import { NzMessageService } from 'ng-zorro-antd/message';
import { defer, finalize, Observable } from 'rxjs';

type SpinnerScopeMap = Readonly<Record<string, number>>;

/**
 * Global + scoped application spinner.
 *
 * Backward compatible:
 *   show()
 *   hide()
 *   showSpinner()
 *   showMessage()
 *
 * New scoped API:
 *   show('order-list:download')
 *   hide('order-list:download')
 *   isLoading('order-list:download')
 *
 * Every scope is request-counted, so one completed request cannot hide
 * a spinner while another request in the same scope is still running.
 */
@Injectable({
  providedIn: 'root',
})
export class SpinnerService {
  private readonly messageService = inject(NzMessageService);

  private readonly scopes = signal<SpinnerScopeMap>({});

  readonly showSpinner = computed(() => Object.values(this.scopes()).some((count) => count > 0));

  show(scope = 'global'): void {
    const current = this.scopes();

    this.scopes.set({
      ...current,
      [scope]: (current[scope] ?? 0) + 1,
    });
  }
  /**
   * Recommended API for HTTP calls. The spinner is always released through finalize(),
   * including error, unsubscribe and component-destroy paths.
   */
  track<T>(source: Observable<T>, scope = 'global'): Observable<T> {
    return defer(() => {
      this.show(scope);
      return source.pipe(finalize(() => this.hide(scope)));
    });
  }

  hide(scope = 'global'): void {
    const current = this.scopes();
    const nextCount = Math.max(0, (current[scope] ?? 0) - 1);

    if (nextCount === 0) {
      const { [scope]: _removed, ...rest } = current;
      this.scopes.set(rest);
      return;
    }

    this.scopes.set({
      ...current,
      [scope]: nextCount,
    });
  }

  isLoading(scope = 'global'): boolean {
    return (this.scopes()[scope] ?? 0) > 0;
  }

  reset(scope?: string): void {
    if (!scope) {
      this.scopes.set({});
      return;
    }

    const current = this.scopes();
    const { [scope]: _removed, ...rest } = current;
    this.scopes.set(rest);
  }

  // Modernized message method
  showMessage(message: string, type: 'success' | 'error' | 'info' | 'warning' = 'info') {
    this.messageService.create(type, message, {
      nzDuration: 2000,
      nzAnimate: true,
    });
  }

  getPendingRequestCount(scope = 'global'): number {
    return this.scopes()[scope] ?? 0;
  }
}
